import { motion, useReducedMotion } from "framer-motion";
import { Seo } from "@/components/brand/Seo";
import { ServiceShowcase } from "@/components/brand/ServiceShowcase";
import {
  BorderBox,
  DarkSection,
  DotList,
  Eyebrow,
  GhostButton,
  OrangeButton,
  Reveal,
  Section,
} from "@/components/brand/Bits";
import { ABOUT, CONTACT, HERO, POSITIONING } from "@/data/siteContent";

/** Hand-drawn style underline that draws itself once beneath the accent word. */
function TrustUnderline() {
  const still = useReducedMotion();
  return (
    <svg
      aria-hidden
      viewBox="0 0 200 16"
      preserveAspectRatio="none"
      className="pointer-events-none absolute -bottom-[0.14em] left-0 h-[0.18em] w-full overflow-visible"
    >
      <motion.path
        d="M3 11.5C42 4.5 92 2.8 141 5.6c19 1.1 38 3.3 56 6.4"
        fill="none"
        stroke="#F2811D"
        strokeWidth="6"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
        initial={still ? { pathLength: 1 } : { pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={still ? { duration: 0 } : { duration: 0.9, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
      />
    </svg>
  );
}

export default function Home() {
  return (
    <>
      <Seo
        path="/"
        title="AnyTribe Marketing — Creator & KOL Marketing Agency in Malaysia"
        description="People trust people. AnyTribe connects brands with the right KOLs and creators on Instagram, XiaoHongShu and TikTok to build trust and turn influence into measurable brand growth."
      />

      {/* Hero */}
      <DarkSection
        raw
        className="no-glow relative overflow-hidden pt-16 pb-20 sm:pt-20 md:pt-24 md:pb-28"
        testId="hero-section"
      >
        <span
          aria-hidden
          className="pointer-events-none absolute -right-24 top-[-12%] h-[520px] w-[520px] rounded-full bg-[#F2811D]/25 blur-[120px] sm:-right-16 lg:h-[640px] lg:w-[640px]"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute right-[18%] bottom-[-30%] h-[360px] w-[360px] rounded-full bg-[#FFC466]/10 blur-[110px]"
        />
        <div className="relative max-w-[56rem]">
          <Reveal testId="hero-eyebrow">
            <Eyebrow dark>{POSITIONING}</Eyebrow>
          </Reveal>

          <h1
            className="mt-5 font-heading font-extrabold leading-[0.96] tracking-[-0.02em] text-white text-[clamp(2.5rem,8.6vw,5.5rem)]"
            data-testid="hero-headline"
          >
            <span className="relative inline-block">
              {HERO.headlineParts.lead}
              <span className="relative inline-block text-[#F2811D]">
                {HERO.headlineParts.accent}
                <TrustUnderline />
              </span>
              {HERO.headlineParts.tail}
            </span>
          </h1>

          <Reveal delay={0.12}>
            <p
              className="mt-5 font-heading font-medium leading-[1.15] tracking-[-0.01em] text-white/80 text-[clamp(1.375rem,4.2vw,2.75rem)]"
              data-testid="hero-statement"
            >
              {HERO.statement}
            </p>
          </Reveal>

          <Reveal delay={0.2}>
            <p
              className="mt-7 max-w-xl text-base leading-relaxed text-white/70 md:text-lg"
              data-testid="hero-supporting"
            >
              {HERO.supporting}
            </p>
          </Reveal>

          <Reveal delay={0.28}>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <OrangeButton to="/contact" testId="hero-work-with-us">
                Work With Us
              </OrangeButton>
              <GhostButton to="/services" dark testId="hero-view-services">
                What we do
              </GhostButton>
            </div>
            <p className="mt-7 text-xs text-white/50 sm:text-sm" data-testid="hero-platforms">
              {HERO.platforms}
            </p>
          </Reveal>
        </div>
      </DarkSection>

      {/* About */}
      <Section testId="about-section">
        <div className="grid gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
          <div>
            <Eyebrow>About AnyTribe</Eyebrow>
            <h2 className="mt-5 font-heading text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl">
              {ABOUT.title}
            </h2>
            {ABOUT.paragraphs.map((para) => (
              <p key={para.slice(0, 24)} className="mt-5 text-base leading-relaxed body-text">
                {para}
              </p>
            ))}
            <div className="mt-9">
              <GhostButton to="/about" testId="home-about-link">
                More about us
              </GhostButton>
            </div>
          </div>
          <BorderBox className="lg:mt-16" testId="home-highlights">
            <h3 className="font-heading text-lg font-extrabold">How engagements are shaped</h3>
            <DotList
              className="mt-5"
              items={[
                "Campaign-based creator activation for a launch, promo or seasonal push.",
                "Monthly always-on creator management for sustained category visibility.",
                "Product launch and event support, with Salt & Soul on the ground.",
                "An integrated creator and growth retainer covering the whole funnel.",
              ]}
              testId="home-highlights-list"
            />
          </BorderBox>
        </div>
      </Section>

      <ServiceShowcase />

      {/* Closing CTA */}
      <DarkSection testId="home-cta-section">
        <div className="grid gap-9 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
          <div>
            <Eyebrow dark>Let's work together</Eyebrow>
            <h2 className="mt-5 font-heading text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl">
              Tell us the objective, the window and a budget range.
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/85">
              We'll return a creator mix, content plan and cost per outcome — not a rate card.
              <br />
              Reach us at {CONTACT.email} or {CONTACT.phone}.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <OrangeButton to="/contact" testId="home-cta-contact">
              Work With Us
            </OrangeButton>
            <GhostButton to="/join" dark testId="home-cta-join">
              Creators: join the pool
            </GhostButton>
          </div>
        </div>
        <div className="mt-10 border-t border-white/12 pt-7">
          <DotList
            dark
            items={[
              "Brands: tell us the objective and we'll return a creator mix by tier.",
              "Creators: apply in two minutes and we'll match you to campaigns your audience cares about.",
            ]}
            testId="home-cta-list"
          />
        </div>
      </DarkSection>
    </>
  );
}
